import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/cart";
import { api, formatMoney } from "../lib/api";
import { brandFor, darkLogoFor } from "../lib/brand";
import Navbar from "../components/Navbar";
import { ShoppingBag, ChevronRight, Trash2, ChevronLeft, Search, Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, remove, total } = useCart();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  // Filter cart items by SKU / name — useful when the cart has 20+ rows and
  // the user wants to find a single part quickly. Empty query = all items.
  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((i) =>
      (i.sku || "").toLowerCase().includes(needle) ||
      (i.name || "").toLowerCase().includes(needle),
    );
  }, [items, q]);

  return (
    <div>
      <Navbar />

      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/catalog">Domov</Link> / <Link to="/catalog">Katalóg</Link> /{" "}
          <span style={{ color: "var(--text)" }}>Nákupný košík</span>
        </div>
      </div>

      <main className="max-w-[1200px] mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide">Nákupný košík</h1>
        <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

        <AddProductSearch />

        {items.length === 0 ? (
          <div className="card-elev p-12 text-center" data-testid="cart-empty">
            <ShoppingBag size={44} className="mx-auto mb-3" style={{ color: "var(--text-disabled)" }} />
            <h3 className="font-bold text-lg mb-2">Váš košík je prázdny.</h3>
            <p className="text-sm mb-5" style={{ color: "var(--text-mute)" }}>
              Pokračujte v nákupe a nájdite vhodné diely.
            </p>
            <Link to="/catalog" className="btn-primary"><ChevronLeft size={14} /> Späť do katalógu</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start">
            <section className="card-elev overflow-hidden" data-testid="cart-table">
              <div className="section-title flex items-center justify-between">
                <span>Položky v košíku ({items.length})</span>
                {items.length > 3 && (
                  <div className="relative flex items-center" onClick={(e) => e.stopPropagation()}>
                    <Search size={12} style={{ color: "rgba(255,255,255,0.7)", position: "absolute", left: 8 }} />
                    <input
                      type="text"
                      placeholder="Hľadať v košíku..."
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      className="text-xs pl-7 pr-2 py-1 rounded bg-white text-gray-800 outline-none w-48"
                      data-testid="cart-search"
                    />
                  </div>
                )}
              </div>
              <div className="overflow-x-auto bg-white">
                <table className="table-b2b">
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}></th>
                      <th>Kód / Názov</th>
                      <th style={{ width: 110 }}>Množstvo</th>
                      <th style={{ width: 110 }} className="text-right">Jedn. cena</th>
                      <th style={{ width: 130 }} className="text-right">Spolu</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && q && (
                      <tr><td colSpan={6} className="text-center py-6 text-sm" style={{ color: "var(--text-mute)" }}>
                        Žiadna zhoda v košíku pre hľadaný kód.
                      </td></tr>
                    )}
                    {filtered.map((i) => {
                      const brand = brandFor(i);
                      return (
                        <tr key={i.product_id} data-testid={`cart-row-${i.sku}`}>
                          <td>
                            <div className="w-14 h-14 flex items-center justify-center border" style={{ borderColor: "var(--border)", background: "#fff" }}>
                              {i.image_url ? (
                                <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />
                              ) : brand ? (
                                <img src={darkLogoFor(brand)} alt={brand.label} className="max-h-[60%] max-w-[60%] object-contain opacity-80" />
                              ) : (
                                <span className="text-[10px] font-bold" style={{ color: "var(--text-disabled)" }}>OEM</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <Link
                              to={`/product/${i.product_id}`}
                              className="font-bold text-base hover:underline"
                              style={{ color: "var(--brand)" }}
                            >
                              {i.name}
                            </Link>
                            <div className="font-mono text-sm font-semibold mt-0.5" style={{ color: "var(--text)" }}>
                              Kód: {i.sku}
                            </div>
                          </td>
                          <td>
                            <div className="inline-flex items-center border" style={{ borderColor: "var(--border-hi)", borderRadius: 3, background: "#fff" }}>
                              <button
                                className="w-8 h-8 hover:bg-[color:var(--surface-elev)] text-base font-bold"
                                onClick={() => updateQty(i.product_id, i.quantity - 1)}
                                data-testid={`dec-${i.sku}`}
                              >−</button>
                              <input
                                type="number" min="1" value={i.quantity}
                                onChange={(e) => updateQty(i.product_id, parseInt(e.target.value) || 1)}
                                className="w-12 text-center bg-transparent outline-none text-base font-bold border-x"
                                style={{ borderColor: "var(--border-hi)", height: 32 }}
                                data-testid={`qty-${i.sku}`}
                              />
                              <button
                                className="w-8 h-8 hover:bg-[color:var(--surface-elev)] text-base font-bold"
                                onClick={() => updateQty(i.product_id, i.quantity + 1)}
                                data-testid={`inc-${i.sku}`}
                              >+</button>
                            </div>
                          </td>
                          <td className="text-right font-mono text-base">{formatMoney(i.unit_price)}</td>
                          <td className="text-right font-mono font-bold text-base" style={{ color: "var(--brand)" }}>
                            {formatMoney(i.unit_price * i.quantity)}
                          </td>
                          <td>
                            <button
                              onClick={() => remove(i.product_id)}
                              className="btn-danger !py-1 !px-2"
                              data-testid={`remove-${i.sku}`}
                              title="Odstrániť"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <aside className="card-elev overflow-hidden lg:sticky lg:top-4" data-testid="cart-summary">
              <div className="section-title">Súhrn</div>
              <div className="p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: "var(--text-mute)" }}>Medzisúčet (netto):</span>
                  <span className="font-mono">{formatMoney(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: "var(--text-mute)" }}>Doprava:</span>
                  <span className="text-xs italic" style={{ color: "var(--text-mute)" }}>Podľa ponuky</span>
                </div>
                <div className="border-t pt-2 flex justify-between items-baseline" style={{ borderColor: "var(--border)" }}>
                  <span className="font-bold uppercase">Spolu:</span>
                  <span className="font-bold text-lg" style={{ color: "var(--brand)" }} data-testid="cart-total">
                    {formatMoney(total)}
                  </span>
                </div>

                <div className="text-[11px] pt-1" style={{ color: "var(--out-stock)" }}>
                  Ceny sú netto, neobsahujú DPH.
                </div>
              </div>
              <div className="p-4 pt-0 space-y-2">
                <button
                  onClick={() => navigate("/checkout")}
                  className="btn-primary w-full"
                  data-testid="proceed-checkout-button"
                >
                  Pokračovať k pokladni <ChevronRight size={14} />
                </button>
                <Link to="/catalog" className="btn-ghost w-full justify-center">
                  <ChevronLeft size={12} /> Pokračovať v nákupe
                </Link>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

// Inline product search — lets the customer add more items without leaving
// the cart. Debounced; a monotonic sequence counter drops stale responses so
// out-of-order API replies can't overwrite the newest results.
function AddProductSearch() {
  const { add } = useCart();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) {
      seq.current++;
      setResults([]);
      setOpen(false);
      setSearching(false);
      return;
    }
    const id = setTimeout(async () => {
      const mySeq = ++seq.current;
      setSearching(true);
      try {
        const { data } = await api.get("/products", { params: { q: needle, limit: 8 } });
        if (mySeq !== seq.current) return;
        setResults(data.items || []);
        setOpen(true);
      } catch {
        if (mySeq === seq.current) { setResults([]); setOpen(true); }
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const addItem = (p) => {
    add(p, 1);
    toast.success(`${p.sku} pridané do košíka`);
  };

  return (
    <div className="card-elev mb-5" style={{ overflow: "visible" }} data-testid="cart-add-search">
      <div className="section-title">Pridať produkt — hľadanie kódu alebo názvu</div>
      <div className="p-3 bg-white relative" ref={boxRef}>
        <div
          className="flex items-center gap-2 border rounded px-3 py-2"
          style={{ borderColor: "var(--border-hi)" }}
        >
          <Search size={16} style={{ color: "var(--text-mute)" }} />
          <input
            type="text"
            placeholder="Napr. 06A115561B alebo olejový filter..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => { if (results.length > 0 || (q.trim().length >= 2 && !searching)) setOpen(true); }}
            className="flex-1 outline-none text-sm bg-transparent"
            data-testid="cart-add-search-input"
          />
          {searching && <span className="text-xs" style={{ color: "var(--text-mute)" }}>Hľadám...</span>}
          {q && (
            <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }} data-testid="cart-add-search-clear">
              <X size={14} />
            </button>
          )}
        </div>

        {open && (
          <div
            className="absolute left-3 right-3 z-30 bg-white border shadow-lg max-h-[360px] overflow-y-auto"
            style={{ borderColor: "var(--border-hi)", top: "calc(100% - 6px)" }}
            data-testid="cart-add-search-results"
          >
            {results.length === 0 ? (
              <div className="p-4 text-sm text-center" style={{ color: "var(--text-mute)" }}>
                Žiadne výsledky.
              </div>
            ) : (
              results.map((p) => {
                const noPrice = !p.your_price || p.your_price === 0;
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-2 border-b hover:bg-slate-50"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`cart-add-result-${p.sku}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-mono text-sm font-bold" style={{ color: "var(--brand)" }}>{p.sku}</span>
                      <div className="text-xs truncate" style={{ color: "var(--text-mute)" }}>{p.name}</div>
                    </div>
                    <div className="font-mono text-sm font-bold whitespace-nowrap" style={{ color: "var(--brand)" }}>
                      {noPrice ? "Ponuka" : formatMoney(p.your_price)}
                    </div>
                    <button
                      onClick={() => addItem(p)}
                      disabled={noPrice}
                      className="btn-primary !py-1.5 !px-3 text-xs font-bold whitespace-nowrap"
                      title={noPrice ? "Cena nie je zadaná" : "Do košíka"}
                      data-testid={`cart-add-btn-${p.sku}`}
                    >
                      <Plus size={13} /> Do košíka
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
